import { useState } from 'react';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import ContactResultsTable from 'components/acturis/ContactResultsTable';
import ContactSearchBar from 'components/acturis/ContactSearchBar';
import SelectSearch from 'components/fields/SelectSearch';
import { useContactSearch } from 'hooks/useContactSearch';
import { getErrorMessage } from 'utils/apiFieldErrors';
import { apiAddContactRelatedContact } from 'api/acturis/client-center';

import {
  CLIENT_CENTER_CUSTOMER_RELATED_CONTACTS_KEY,
  useClientCenterRelatedContactOptions,
} from '../../hooks';

import type { ActurisAddRelatedContactPayload } from 'api/acturis/client-center';
import type { ActurisContact, ContactSearchType } from 'api/quote-marketplace';

const PAGE_SIZE = 10;

const TABLE_HEADERS = ['Client Name', 'Client Address', 'Postal Code'];

const INLINE_FIELDS = new Set([
  'related_contact_ref',
  'relationship',
  'interested_party_type',
]);

interface AddRelatedContactModalProps {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
}

const AddRelatedContactModal = ({
  customerId,
  isOpen,
  onClose,
}: AddRelatedContactModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const [searchType, setSearchType] = useState<ContactSearchType>('name');
  const [searchValue, setSearchValue] = useState('');
  const [selectedContact, setSelectedContact] = useState<ActurisContact | null>(
    null,
  );
  const [relationship, setRelationship] = useState('');
  const [interestedPartyType, setInterestedPartyType] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nonFieldError, setNonFieldError] = useState<string | null>(null);

  const {
    results,
    status,
    isSearching,
    triggerSearch,
    clearSearch,
    activeSearch,
  } = useContactSearch({ enabled: isOpen });

  const {
    relationshipTypes,
    interestedPartyTypes,
    isLoading: isOptionsLoading,
    error: optionsError,
  } = useClientCenterRelatedContactOptions(customerId, isOpen);

  const searchResults = results.filter(
    (contact) => contact.contact_ref !== customerId,
  );
  const interestedPartyOptions = interestedPartyTypes.filter(
    (option) => option.relationship === relationship,
  );
  const hasActiveSearch = activeSearch.trim().length > 0;

  const resetForm = () => {
    setSearchType('name');
    setSearchValue('');
    setSelectedContact(null);
    setRelationship('');
    setInterestedPartyType('');
    setErrors({});
    setNonFieldError(null);
    clearSearch();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleError = (error: any) => {
    toast({ title: 'Could not add related contact', status: 'error' });

    if (error?.error === 'No response' || error?.error === 'Unhandled error') {
      const serverMessage = error.message?.response?.data?.error;
      setNonFieldError(
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : 'Something went wrong adding this related contact. Please try again.',
      );
      return;
    }

    if (error?.error === 'not_added') {
      setNonFieldError(
        ["Couldn't add this related contact.", error.message]
          .filter(Boolean)
          .join(' '),
      );
      return;
    }

    if (error?.error === 'already_related') {
      setNonFieldError(
        'These contacts are already related with this relationship.',
      );
      return;
    }

    const formatted: Record<string, string> = {};
    const topLevel: string[] = [];

    Object.entries(error ?? {}).forEach(([field, messages]) => {
      const message = getErrorMessage(messages);
      if (!message) return;
      if (INLINE_FIELDS.has(field)) {
        formatted[field] = message;
      } else {
        topLevel.push(message);
      }
    });

    setErrors(formatted);
    setNonFieldError(topLevel.length ? topLevel.join(' ') : null);
  };

  const addMutation = useMutation({
    mutationFn: (payload: ActurisAddRelatedContactPayload) =>
      apiAddContactRelatedContact(customerId, payload),
    onSuccess: (relatedContacts, payload) => {
      queryClient.setQueryData(
        [CLIENT_CENTER_CUSTOMER_RELATED_CONTACTS_KEY, customerId],
        relatedContacts,
      );
      queryClient.invalidateQueries({
        queryKey: [
          CLIENT_CENTER_CUSTOMER_RELATED_CONTACTS_KEY,
          payload.related_contact_ref,
        ],
      });
      toast({ title: 'Related contact added', status: 'success' });
      handleClose();
    },
    onError: handleError,
  });

  const canSave =
    !!selectedContact &&
    !!relationship &&
    !optionsError &&
    !addMutation.isPending;

  const handleSearch = () => {
    triggerSearch(searchValue, searchType, 1, PAGE_SIZE);
  };

  const handleResetSearch = () => {
    setSearchValue('');
    clearSearch();
  };

  const clearErrors = (...fields: string[]) => {
    setErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => delete next[field]);
      return next;
    });
    setNonFieldError(null);
  };

  const handleContactChange = (contact: ActurisContact | null) => {
    setSelectedContact(contact);
    clearErrors('related_contact_ref');
  };

  const handleRelationshipChange = (value: string | number | null) => {
    setRelationship(value ? String(value) : '');
    setInterestedPartyType('');
    clearErrors('relationship', 'interested_party_type');
  };

  const handleInterestedPartyTypeChange = (value: string | number | null) => {
    setInterestedPartyType(value ? String(value) : '');
    clearErrors('interested_party_type');
  };

  const handleSubmit = () => {
    if (!canSave || !selectedContact) return;
    setErrors({});
    setNonFieldError(null);
    addMutation.mutate({
      related_contact_ref: selectedContact.contact_ref,
      relationship,
      interested_party_type: interestedPartyType,
    });
  };

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <Flex justify="center" py="4">
          <Spinner />
        </Flex>
      );
    }

    if (hasActiveSearch && status === 'error') {
      return (
        <Text color="red.500" fontSize="sm">
          Search failed. Please try again.
        </Text>
      );
    }

    if (!hasActiveSearch || status !== 'complete') {
      return null;
    }

    if (searchResults.length === 0) {
      return (
        <Text color={labelColor} fontSize="sm">
          No contacts found.
        </Text>
      );
    }

    return (
      <Box maxH="320px" overflowY="auto">
        <ContactResultsTable
          headers={TABLE_HEADERS}
          contacts={searchResults}
          onRowClick={handleContactChange}
        />
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!addMutation.isPending}
      closeOnEsc={!addMutation.isPending}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add related contact</ModalHeader>
        <ModalCloseButton isDisabled={addMutation.isPending} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {optionsError && (
              <Alert status="error" fontSize="sm" borderRadius="md">
                <AlertIcon />
                Could not load relationship options. Please try again.
              </Alert>
            )}
            {nonFieldError && (
              <Alert status="error" fontSize="sm" borderRadius="md">
                <AlertIcon />
                {nonFieldError}
              </Alert>
            )}

            <FormControl isRequired isInvalid={!!errors.related_contact_ref}>
              <FormLabel fontSize="sm">Contact</FormLabel>
              {selectedContact ? (
                <Flex
                  align="center"
                  justify="space-between"
                  gap="2"
                  px="4"
                  py="2"
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <Box overflow="hidden">
                    <Text color={textColor} fontWeight="600" noOfLines={1}>
                      {selectedContact.name}
                    </Text>
                    <Text color={labelColor} fontSize="sm" noOfLines={1}>
                      {[selectedContact.address, selectedContact.postcode]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </Box>
                  <Button
                    variant="link"
                    size="sm"
                    flexShrink={0}
                    onClick={() => handleContactChange(null)}
                    isDisabled={addMutation.isPending}
                  >
                    Change
                  </Button>
                </Flex>
              ) : (
                <VStack align="stretch" spacing={3}>
                  <ContactSearchBar
                    searchType={searchType}
                    onSearchTypeChange={setSearchType}
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    onSearch={handleSearch}
                    onReset={handleResetSearch}
                    hasActiveSearch={hasActiveSearch}
                    isDisabled={addMutation.isPending}
                  />
                  {renderSearchResults()}
                </VStack>
              )}
              <FormErrorMessage>{errors.related_contact_ref}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.relationship}>
              <FormLabel fontSize="sm">Relationship</FormLabel>
              <SelectSearch
                name="relationship"
                options={relationshipTypes}
                value={relationship}
                onChange={handleRelationshipChange}
                placeholder="Select relationship"
                isLoading={isOptionsLoading}
                isDisabled={addMutation.isPending}
                isInvalid={!!errors.relationship}
              />
              <FormErrorMessage>{errors.relationship}</FormErrorMessage>
            </FormControl>

            {interestedPartyOptions.length > 0 && (
              <FormControl isInvalid={!!errors.interested_party_type}>
                <FormLabel fontSize="sm">Interested party type</FormLabel>
                <SelectSearch
                  name="interested_party_type"
                  options={interestedPartyOptions}
                  value={interestedPartyType}
                  onChange={handleInterestedPartyTypeChange}
                  isDisabled={addMutation.isPending}
                  placeholder="Select interested party type"
                  isInvalid={!!errors.interested_party_type}
                  allowClear
                />
                <FormErrorMessage>
                  {errors.interested_party_type}
                </FormErrorMessage>
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            mr={3}
            onClick={handleClose}
            isDisabled={addMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            isLoading={addMutation.isPending}
            isDisabled={!canSave}
            onClick={handleSubmit}
          >
            Add contact
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddRelatedContactModal;
